import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useNavigate } from 'react-router-dom';
import { getMyAppointments } from '../services/doctorsApi';
import { getMyPatients } from '../services/patientApi';
import { getAllPatientsAdmin, getAllDoctorsAdmin } from '../services/adminApi';
import { getInventoryStats } from '../services/inventoryApi';
import { getServices } from '../services/servicesApi';
import DoctorDashboard from './DoctorDashboard';
import RecentActivityCard from '../components/RecentActivityCard';
import { API_BASE_URL as BASE_URL } from '../services/apiBaseUrl';
import {
  Icon, PageHero, PanelHeader, Pill, SectionHeader, StatCard, QuickAction, SearchBar,
} from '../components/ui';

/* Role → hero badge tone (matches the avatar colours used in the Navbar). */
const ROLE_META = {
  ADMIN:        { label: 'Admin',        tone: 'indigo' },
  DOCTOR:       { label: 'Doctor',       tone: 'violet' },
  PATIENT:      { label: 'Patient',      tone: 'amber'  },
  doctor:       { label: 'Doctor',       tone: 'violet' },
  patient:      { label: 'Patient',      tone: 'amber'  },
  staff:        { label: 'Staff',        tone: 'mint'   },
  tenant_admin: { label: 'Tenant Admin', tone: 'cyan'   },
  system_admin: { label: 'System Admin', tone: 'blue'   },
  super_admin:  { label: 'Super Admin',  tone: 'indigo' },
};

const STATUS_META = {
  CONFIRMED: { label: 'Confirmed', tone: 'mint'   },
  PENDING:   { label: 'Pending',   tone: 'amber'  },
  CANCELLED: { label: 'Cancelled', tone: 'red'    },
  COMPLETED: { label: 'Completed', tone: 'indigo' },
};

const RX_META = {
  PENDING:  { label: 'Pending',  tone: 'amber' },
  APPROVED: { label: 'Approved', tone: 'mint'  },
  REJECTED: { label: 'Rejected', tone: 'red'   },
};

/* ── tiny helpers ─────────────────────────────────────────────────── */

const SkeletonLine = ({ w = '60%', h = 13 }) => (
  <div className="skeleton skeleton-line" style={{ width: w, height: h, borderRadius: 6, marginBottom: 8 }} />
);

const SkeletonRows = ({ avatarRadius = 12 }) => (
  <div>
    {[1, 2, 3].map(i => (
      <div key={i} className="ds-row" style={{ alignItems: 'flex-start' }}>
        <div className="skeleton" style={{ width: 44, height: 44, borderRadius: avatarRadius, flexShrink: 0 }} />
        <div className="ds-row-main">
          <SkeletonLine w="55%" h={13} />
          <SkeletonLine w="70%" h={11} />
        </div>
      </div>
    ))}
  </div>
);

const LoadError = ({ what, onRetry }) => (
  <div className="ds-empty">
    <span>
      Could not load {what}.{' '}
      <button className="ds-retry" onClick={onRetry}>Retry</button>
    </span>
  </div>
);

const initials = (name = '') =>
  name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).filter(Boolean).join('') || '?';

/* ── Appointments mini-card ───────────────────────────────────────── */

const MODE_LABEL = {
  online:    { icon: 'video',  label: 'Video consultation' },
  'walk-in': { icon: 'mapPin', label: 'Clinic visit' },
};

const ApptRow = ({ appt, onViewResults }) => {
  // Format date compactly: "Mon, Jun 1"
  const dateStr = appt.appointmentDate
    ? new Date(appt.appointmentDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    : '—';
  const sm = STATUS_META[appt.status] || STATUS_META['CONFIRMED'];
  const showViewResults = appt.consultationAvailable === true || appt.status === 'COMPLETED';
  const mode = MODE_LABEL[appt.mode];

  return (
    <div className="ds-row" style={{ alignItems: 'flex-start' }}>
      <div className="ds-row-avatar">{initials(appt.doctorName || 'Doctor')}</div>

      <div className="ds-row-main">
        <div className="ds-row-title">{appt.doctorName || 'Doctor'}</div>

        {(appt.patientName || appt.name) && (
          <div className="ds-row-sub">
            For {appt.patientName || appt.name}
            {appt.relation && appt.relation !== 'SELF' && (
              <span style={{ color: 'var(--ds-text-muted)' }}> ({appt.relation.toLowerCase()})</span>
            )}
          </div>
        )}

        {mode && (
          <div className="ds-row-meta" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Icon name={mode.icon} size={12} />
            {appt.mode === 'walk-in' && appt.doctorClinic ? appt.doctorClinic : mode.label}
          </div>
        )}
      </div>

      <div className="ds-row-aside">
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ds-text)', whiteSpace: 'nowrap' }}>{dateStr}</div>
        {appt.slotTime && <div className="ds-row-time">{appt.slotTime}</div>}
        <Pill tone={sm.tone}>{sm.label}</Pill>
        {showViewResults && (
          <button className="ds-view-all" style={{ padding: '2px 4px' }}
            onClick={() => onViewResults && onViewResults(appt)}>
            View results <Icon name="arrowRight" size={12} />
          </button>
        )}
      </div>
    </div>
  );
};

const AppointmentsCard = ({ navigate }) => {
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await getMyAppointments();
      const now = new Date();
      const sorted = [...data].sort((a, b) =>
        new Date(b.appointmentDate) - new Date(a.appointmentDate)
      );
      const upcoming = sorted.filter(a => new Date(a.appointmentDate) >= now);
      setAppts((upcoming.length ? upcoming : sorted).slice(0, 3));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleViewResults = (appt) => {
    navigate(`/appointments/${appt.id}/results`);
  };

  return (
    <div className="ds-panel ds-panel--fixed">
      <PanelHeader
        icon="calendar" title="Appointments"
        count={!loading && !error ? appts.length : undefined}
        onViewAll={() => navigate('/appointments')}
      />

      <div className="ds-panel-body">
        {loading && <SkeletonRows />}

        {!loading && error && <LoadError what="appointments" onRetry={load} />}

        {!loading && !error && appts.length === 0 && (
          <div className="ds-empty">
            <span className="ds-icon-box ds-icon-box--lg tone-indigo"><Icon name="calendar" size={24} /></span>
            No upcoming appointments
            <button className="ds-btn ds-btn--sm" onClick={() => navigate('/services/online-consultation')}>
              <Icon name="plus" size={14} strokeWidth={2.4} /> Book a consultation
            </button>
          </div>
        )}

        {!loading && !error && appts.length > 0 && (
          appts.map((a, i) => <ApptRow key={a.id || i} appt={a} onViewResults={handleViewResults} />)
        )}
      </div>

      {!loading && !error && appts.length > 0 && (
        <div className="ds-panel-foot">
          <button className="ds-btn ds-btn--dashed" onClick={() => navigate('/appointments')}>
            <Icon name="plus" size={15} strokeWidth={2.4} /> Book a new appointment
          </button>
        </div>
      )}
    </div>
  );
};

/* ── Prescriptions mini-card ──────────────────────────────────────── */

const RxRow = ({ rx }) => {
  const m = RX_META[rx.status] || RX_META['PENDING'];
  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Math.round((Date.now() - new Date(iso)) / 60000);
    if (diff < 60)   return `${diff}m ago`;
    if (diff < 1440) return `${Math.round(diff / 60)}h ago`;
    return `${Math.round(diff / 1440)}d ago`;
  };

  return (
    <div className="ds-row">
      <span className="ds-icon-box tone-cyan"><Icon name={rx.fileType === 'application/pdf' ? 'file' : 'rx'} size={18} /></span>
      <div className="ds-row-main">
        <div className="ds-row-title">{rx.fileName || 'Prescription'}</div>
        {rx.assignedDoctorName ? (
          <div className="ds-row-sub">{rx.assignedDoctorName}</div>
        ) : rx.status === 'PENDING' ? (
          <div className="ds-row-sub" style={{ color: 'var(--ds-text-muted)' }}>Awaiting assignment</div>
        ) : null}
      </div>
      <div className="ds-row-aside">
        <Pill tone={m.tone}>{m.label}</Pill>
        <span className="ds-row-time">{timeAgo(rx.uploadedAt)}</span>
      </div>
    </div>
  );
};

const PrescriptionsCard = ({ navigate, token, tenantId }) => {
  const [rxList, setRxList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !tenantId) return;
    try {
      const res = await fetch(`${BASE_URL}/pharmacy/prescriptions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.content ?? []);
      setRxList([...list].sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)).slice(0, 3));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [token, tenantId]);

  useEffect(() => { load(); }, [load]);

  const pending  = rxList.filter(r => r.status === 'PENDING').length;
  const approved = rxList.filter(r => r.status === 'APPROVED').length;

  return (
    <div className="ds-panel ds-panel--fixed">
      <PanelHeader
        icon="rx" title="Prescriptions"
        count={!loading && !error ? rxList.length : undefined}
        onViewAll={() => navigate('/prescriptions')}
      />

      {/* Mini stat row */}
      {!loading && !error && rxList.length > 0 && (
        <div style={{ display: 'flex', gap: 10, margin: '4px 0 6px', flexShrink: 0 }}>
          {[
            { label: 'Pending', val: pending, tone: 'amber' },
            { label: 'Approved', val: approved, tone: 'mint' },
          ].map(s => (
            <div key={s.label} className={`tone-${s.tone}`} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 12px', background: 'var(--tone-bg)', borderRadius: 10,
            }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tone-fg)' }}>{s.label}</span>
              <span style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 17, color: 'var(--ds-text)' }}>{s.val}</span>
            </div>
          ))}
        </div>
      )}

      <div className="ds-panel-body">
        {loading && <SkeletonRows />}

        {!loading && error && <LoadError what="prescriptions" onRetry={load} />}

        {!loading && !error && rxList.length === 0 && (
          <div className="ds-empty">
            <span className="ds-icon-box ds-icon-box--lg tone-cyan"><Icon name="rx" size={24} /></span>
            No prescriptions uploaded yet
            <button className="ds-btn ds-btn--sm" onClick={() => navigate('/pharmacy')}>
              Go to Pharmacy
            </button>
          </div>
        )}

        {!loading && !error && rxList.length > 0 && (
          rxList.map((rx, i) => <RxRow key={rx.id || i} rx={rx} />)
        )}
      </div>

      {!loading && !error && rxList.length > 0 && (
        <div className="ds-panel-foot">
          <button className="ds-btn ds-btn--soft ds-btn--block" onClick={() => navigate('/prescriptions')}>
            View all prescriptions
          </button>
        </div>
      )}
    </div>
  );
};

/* ── Active Medicines card ────────────────────────────────────────── */

// const MedRow = ({ med }) => {
//   const daysLeft = med.endDate
//     ? Math.max(0, Math.ceil((new Date(med.endDate) - new Date()) / 86400000))
//     : null;
//   const progress = med.durationDays && daysLeft !== null
//     ? Math.round(((med.durationDays - daysLeft) / med.durationDays) * 100)
//     : 100;
//   const color = !daysLeft || daysLeft === null
//     ? '#1a56db'
//     : daysLeft <= 5  ? '#ef4444'
//     : daysLeft <= 14 ? '#f59e0b'
//     : '#1a56db';
//   const detailText = daysLeft === null
//     ? `${med.dosage} · Ongoing`
//     : `${med.dosage} · ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`;

//   return (
//     <div style={{
//       display: 'flex', alignItems: 'center', gap: 10,
//       padding: '12px 0', borderBottom: '1px solid #f3f4f6',
//     }}>
//       <div style={{
//         width: 38, height: 38, borderRadius: 10, flexShrink: 0,
//         background: '#eef2ff', display: 'flex', alignItems: 'center',
//         justifyContent: 'center', fontSize: 20,
//         border: '1px solid #e0e7ff',
//       }}>
//         💊
//       </div>
//       <div style={{ flex: 1, minWidth: 0 }}>
//         <div style={{
//           fontFamily: 'Sora, sans-serif', fontWeight: 600, fontSize: 13,
//           color: '#1e1b4b', marginBottom: 2,
//           whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
//         }}>
//           {med.name}
//         </div>
//         <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'DM Sans, sans-serif', marginBottom: 5 }}>
//           {detailText}
//         </div>
//         {med.durationDays && daysLeft !== null && (
//           <div style={{ height: 4, background: '#f3f4f6', borderRadius: 9999, overflow: 'hidden' }}>
//             <div style={{
//               height: '100%', width: `${progress}%`,
//               background: color, borderRadius: 9999,
//             }} />
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// const ActiveMedicinesCard = ({ navigate, token, tenantId }) => {
//   const [medicines, setMedicines] = useState([]);
//   const [loading, setLoading]     = useState(true);
//   const [error, setError]         = useState(null);

//   const load = useCallback(async () => {
//     if (!token || !tenantId) return;
//     setError(null);
//     try {
//       const res = await fetch(`${BASE_URL}/patient/medicines/active`, {
//         headers: {
//           Authorization: `Bearer ${token}`,
//           'X-Tenant-ID': tenantId,
//           'Content-Type': 'application/json',
//         },
//       });
//       if (!res.ok) {
//         // Backend's GlobalExceptionHandler returns { message, status, timestamp }.
//         // Surface that real message instead of a generic string so failures are debuggable.
//         let message = `Request failed (${res.status})`;
//         try {
//           const body = await res.json();
//           if (body && body.message) message = body.message;
//         } catch { /* non-JSON error body — keep the generic fallback */ }
//         throw new Error(message);
//       }
//       const data = await res.json();
//       setMedicines(Array.isArray(data) ? data.slice(0, 3) : []);
//     } catch (e) {
//       setError(e.message || 'Could not load medicines.');
//     } finally {
//       setLoading(false);
//     }
//   }, [token, tenantId]);

//   useEffect(() => { load(); }, [load]);

//   return (
//     <div style={{
//       background: '#fff', border: '1px solid #e0e7ff', borderRadius: 18,
//       padding: '18px 16px',
//       boxShadow: '0 2px 12px rgba(26,86,219,0.06)',
//     }}>
//       <SectionHeader
//         icon="💊" title="Active Medicines"
//         count={!loading && !error ? medicines.length : undefined}
//         onViewAll={() => navigate('/patient/medicines')}
//       />

//       {loading && (
//         <div>
//           {[1, 2, 3].map(i => (
//             <div key={i} style={{ display: 'flex', gap: 10, padding: '12px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'flex-start' }}>
//               <div className="skeleton" style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0 }} />
//               <div style={{ flex: 1, minWidth: 0 }}>
//                 <SkeletonLine w="55%" h={13} />
//                 <SkeletonLine w="35%" h={11} />
//                 <SkeletonLine w="100%" h={4} />
//               </div>
//             </div>
//           ))}
//         </div>
//       )}

//       {!loading && error && (
//         <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
//           {error}{' '}
//           <button onClick={load} style={{ background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontWeight: 600 }}>Retry</button>
//         </div>
//       )}

//       {!loading && !error && medicines.length === 0 && (
//         <div style={{ textAlign: 'center', padding: '24px 0' }}>
//           <div style={{ fontSize: 32, marginBottom: 8 }}>💊</div>
//           <div style={{ fontSize: 13, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif' }}>No active prescriptions</div>
//         </div>
//       )}

//       {!loading && !error && medicines.length > 0 && (
//         <>
//           {medicines.map((m, i) => <MedRow key={m.id || i} med={m} />)}
//           <button
//             onClick={() => navigate('/patient/medicines')}
//             style={{
//               width: '100%', marginTop: 12, padding: '9px 0',
//               fontSize: 12.5, fontWeight: 600, color: '#1a56db',
//               background: '#eff6ff', border: '1px solid #bfdbfe',
//               borderRadius: 10, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
//               transition: 'background .12s',
//             }}
//             onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
//             onMouseLeave={e => e.currentTarget.style.background = '#eff6ff'}
//           >
//             View all medicines
//           </button>
//         </>
//       )}
//     </div>
//   );
// };

/* ── My Patients card ─────────────────────────────────────────────── */

const PatientRow = ({ p }) => {
  const rel = p.relation || (p.isSelf ? 'SELF' : 'OTHER');
  const relLabel = rel.charAt(0) + rel.slice(1).toLowerCase();
  return (
    <div className="ds-row">
      <div className="ds-row-avatar" style={{ borderRadius: '50%' }}>{initials(p.fullName)}</div>
      <div className="ds-row-main">
        <div className="ds-row-title">{p.fullName || 'Unknown'}</div>
        <div className="ds-row-sub">
          {relLabel}
          {p.age ? ` · ${p.age} yrs` : ''}
          {p.gender ? ` · ${p.gender.charAt(0) + p.gender.slice(1).toLowerCase()}` : ''}
        </div>
        {p.phone && <div className="ds-row-meta">{p.phone}</div>}
      </div>
      <Pill tone={rel === 'SELF' ? 'indigo' : 'violet'}>{rel === 'SELF' ? 'You' : relLabel}</Pill>
    </div>
  );
};

const MyPatientsCard = ({ navigate }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await getMyPatients();
      setPatients(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="ds-panel ds-panel--fixed">
      <PanelHeader
        icon="users" title="My Patients"
        count={!loading && !error ? patients.length : undefined}
        onViewAll={() => navigate('/appointments')}
      />

      <div className="ds-panel-body">
        {loading && <SkeletonRows avatarRadius="50%" />}

        {!loading && error && <LoadError what="patients" onRetry={load} />}

        {!loading && !error && patients.length === 0 && (
          <div className="ds-empty">
            <span className="ds-icon-box ds-icon-box--lg tone-violet"><Icon name="users" size={24} /></span>
            No saved patient profiles yet
            <button className="ds-btn ds-btn--sm" onClick={() => navigate('/appointments')}>
              <Icon name="plus" size={14} strokeWidth={2.4} /> Book an appointment
            </button>
          </div>
        )}

        {!loading && !error && patients.length > 0 && (
          patients.map((p, i) => <PatientRow key={p.id || i} p={p} />)
        )}
      </div>
    </div>
  );
};

/* ── Patient service shortcuts ────────────────────────────────────────
   Same tenant service list as the Home page (servicesApi), rendered as a
   compact shortcut strip. ── */
const ServiceShortcuts = ({ tenantId, navigate }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    getServices(tenantId)
      .then(setServices)
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, [tenantId]);

  if (!loading && services.length === 0) return null;

  return (
    <div className="ds-tile-card" style={{ marginBottom: 'var(--ds-gap)', animation: 'fadeUp .4s 120ms ease both' }}>
      <div className="ds-scroller">
        {loading
          ? Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="ds-tile is-disabled">
                <div className="skeleton" style={{ width: 52, height: 52, borderRadius: 15 }} />
                <div className="skeleton" style={{ width: 64, height: 10 }} />
              </div>
            ))
          : services.map(s => {
              const enabled = s.available && s.route;
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`ds-tile${enabled ? '' : ' is-disabled'}`}
                  onClick={() => enabled && navigate(s.route)}
                  title={enabled ? s.title : `${s.title} — coming soon`}
                >
                  <span className="ds-icon-box ds-icon-box--lg" style={{ background: s.bg }}>{s.icon}</span>
                  {s.title}
                </button>
              );
            })}
      </div>
    </div>
  );
};

/* ── Admin dashboard content ─────────────────────────────────────────
   Tenant-wide stat cards (patients, doctors, inventory, tenants), quick
   links into the admin pages, recent activity and an inventory overview —
   all built from data the admin APIs already return (no new endpoints). ── */

const DONUT_COLORS = { inStock: '#6366f1', outOfStock: '#ef4444' };

/* Two-segment donut: in stock vs out of stock. Every segment is also listed
   with its value in the legend, so identity never relies on colour alone. */
const InventoryDonut = ({ inStock = 0, outOfStock = 0, total }) => {
  const R = 64, STROKE = 18, C = 2 * Math.PI * R, GAP = 3;
  const sum = inStock + outOfStock;
  const segs = [
    { key: 'inStock',    label: 'In stock',     value: inStock },
    { key: 'outOfStock', label: 'Out of stock', value: outOfStock },
  ];
  const visible = segs.filter(s => s.value > 0);
  let offset = 0;

  return (
    <div className="ds-donut">
      <svg viewBox="0 0 168 168" role="img" aria-label={`${inStock} in stock, ${outOfStock} out of stock`}>
        <circle cx="84" cy="84" r={R} fill="none" stroke="#f1f0f8" strokeWidth={STROKE} />
        {sum > 0 && visible.map(s => {
          const full = (s.value / sum) * C;
          const len = visible.length > 1 ? Math.max(full - GAP, 0.5) : full;
          const el = (
            <circle
              key={s.key} data-seg cx="84" cy="84" r={R} fill="none"
              stroke={DONUT_COLORS[s.key]} strokeWidth={STROKE}
              strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset}
            >
              <title>{`${s.label}: ${s.value.toLocaleString('en-IN')} (${Math.round((s.value / sum) * 100)}%)`}</title>
            </circle>
          );
          offset += full;
          return el;
        })}
      </svg>
      <div className="ds-donut-center">
        <div className="ds-donut-value">{(total ?? sum).toLocaleString('en-IN')}</div>
        <div className="ds-donut-label">Products</div>
      </div>
    </div>
  );
};

const InventoryOverviewCard = ({ stats, loading, navigate }) => {
  const total   = stats.products ?? 0;
  const out     = stats.outOfStock ?? 0;
  const inStock = stats.inStock ?? Math.max(total - out, 0);

  return (
    <div className="ds-panel ds-panel--fixed">
      <PanelHeader icon="pieChart" title="Inventory Overview" onViewAll={() => navigate('/dashboard/inventory')} />
      {loading ? (
        <div className="ds-donut-wrap">
          <div className="skeleton" style={{ width: 168, height: 168, borderRadius: '50%' }} />
        </div>
      ) : (
        <div className="ds-donut-wrap">
          <InventoryDonut inStock={inStock} outOfStock={out} total={total} />
          <ul className="ds-legend">
            <li>
              <span className="ds-legend-swatch" style={{ background: DONUT_COLORS.inStock }} />
              In stock
              <span className="ds-legend-value">{inStock.toLocaleString('en-IN')}</span>
            </li>
            <li>
              <span className="ds-legend-swatch" style={{ background: DONUT_COLORS.outOfStock }} />
              Out of stock
              <span className="ds-legend-value">{out.toLocaleString('en-IN')}</span>
            </li>
            {stats.prescriptionReq != null && (
              <li className="ds-legend-note">
                Prescription required
                <span className="ds-legend-value">{stats.prescriptionReq.toLocaleString('en-IN')}</span>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const AdminOverview = ({ navigate, token, tenantId, user, tenantCount }) => {
  const [stats, setStats] = useState({
    patients: null, doctors: null,
    products: null, inStock: null, outOfStock: null, prescriptionReq: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!token || !tenantId) return;
    setLoading(true);
    setError(false);
    try {
      const [patients, doctors, inv] = await Promise.all([
        getAllPatientsAdmin(token, tenantId),
        getAllDoctorsAdmin(token, tenantId),
        getInventoryStats(token, tenantId),
      ]);
      setStats({
        patients: patients.length,
        doctors: doctors.length,
        products: inv.total,
        inStock: inv.inStock,
        outOfStock: inv.outOfStock,
        prescriptionReq: inv.prescriptionRequired,
      });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [token, tenantId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ animation: 'fadeUp .4s 120ms ease both' }}>
      {/* KPI row */}
      <div className="ds-stat-grid">
        <StatCard icon="users"       tone="indigo" label="Patients"     value={stats.patients}   loading={loading} onClick={() => navigate('/admin/patients')} />
        <StatCard icon="stethoscope" tone="violet" label="Doctors"      value={stats.doctors}    loading={loading} onClick={() => navigate('/admin/doctors')} />
        <StatCard icon="package"     tone="orange" label="Products"     value={stats.products}   loading={loading} onClick={() => navigate('/dashboard/inventory')} />
        <StatCard icon="alert"       tone="red"    label="Out of stock" value={stats.outOfStock} loading={loading} onClick={() => navigate('/dashboard/inventory')} />
        <StatCard icon="rx"          tone="cyan"   label="Rx required"  value={stats.prescriptionReq} loading={loading} onClick={() => navigate('/dashboard/inventory')} />
        <StatCard icon="building"    tone="mint"   label="Tenants"      value={tenantCount}      onClick={() => navigate('/tenants')} />
      </div>

      {error && (
        <div style={{ fontSize: 12.5, color: 'var(--ds-text-muted)', marginTop: 10 }}>
          Some overview numbers couldn't load.{' '}
          <button className="ds-retry" onClick={load}>Retry</button>
        </div>
      )}

      {/* Quick actions */}
      <div className="ds-section" style={{ marginTop: 28 }}>
        <SectionHeader title="Quick Actions" />
        <div className="ds-quick-grid">
          <QuickAction icon="users"       tone="indigo" label="Manage patients"      onClick={() => navigate('/admin/patients')} />
          <QuickAction icon="stethoscope" tone="violet" label="Manage doctors"       onClick={() => navigate('/admin/doctors')} />
          <QuickAction icon="package"     tone="orange" label="Inventory management" onClick={() => navigate('/dashboard/inventory')} />
          <QuickAction icon="building"    tone="pink"   label="Tenant settings"      onClick={() => navigate('/admin/tenants')} />
        </div>
      </div>

      {/* Activity + overview */}
      <div className="ds-grid-main-side" style={{ marginTop: 28 }}>
        <RecentActivityCard role={user?.role} />
        <InventoryOverviewCard stats={stats} loading={loading} navigate={navigate} />
      </div>
    </div>
  );
};

/* ── Dashboard ────────────────────────────────────────────────────── */

const Dashboard = () => {
  const { user, token } = useAuth();
  const { tenant, tenants } = useTenant();
  const navigate = useNavigate();

  const meta = ROLE_META[user?.role] || ROLE_META['PATIENT'];
  const isPatient = ['PATIENT', 'patient'].includes(user?.role);
  const isDoctor  = ['DOCTOR', 'doctor'].includes(user?.role);
  const isAdmin   = user?.role?.toUpperCase() === 'ADMIN';

  if (isDoctor) {
    return <DoctorDashboard />;
  }

  const firstName = user?.name ? user.name.split(' ')[0] : user?.phone;

  return (
    <div className="page">
      <main className="page-main">

        <PageHero
          badge={<Pill tone={meta.tone}>{meta.label}</Pill>}
          title={<>Good day, {firstName} <span aria-hidden>👋</span></>}
          subtitle={
            isAdmin
              ? "Here's an overview of your multi-tenant healthcare workspace."
              : isPatient
                ? 'Your health journey, simplified. Consultations, medicines and records in one place.'
                : "Here's an overview of your workspace."
          }
          art={isAdmin ? 'admin' : 'patient'}
        >
          {isPatient && (
            <SearchBar
              placeholder="Search doctors, specialties or health concerns…"
              ariaLabel="Search doctors"
              onSubmit={(q) => navigate(q ? `/services/doctors?q=${encodeURIComponent(q)}` : '/services/doctors')}
            />
          )}
        </PageHero>

        {isPatient ? (
          <>
            <ServiceShortcuts tenantId={tenant?.id} navigate={navigate} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ds-gap)', animation: 'fadeUp .4s 200ms ease both' }}>
              <div className="ds-grid-2">
                <AppointmentsCard navigate={navigate} />
                <RecentActivityCard role={user?.role} />
              </div>
              <div className="ds-grid-2">
                <MyPatientsCard navigate={navigate} />
                <PrescriptionsCard navigate={navigate} token={token} tenantId={tenant?.id} />
              </div>
            </div>
          </>
        ) : isAdmin ? (
          <AdminOverview
            navigate={navigate} token={token} tenantId={tenant?.id}
            user={user} tenantCount={tenants?.length ?? null}
          />
        ) : (
          <div className="placeholder-area">
            <div className="placeholder-icon">🏗️</div>
            <p className="placeholder-text">Build out your {meta.label} dashboard content here.</p>
          </div>
        )}

      </main>
    </div>
  );
};

export default Dashboard;
